import Api from '@sendsprint/api-types';
import ClientApi from '@src/types/client';
import { useAccount, useMixpanel } from '@src/contexts';
import { getState } from '@src/utils/stateStorage';
import { useCallback, useEffect, useState } from 'react';
import { mixpanelEvents } from '@src/types/mixpanel';
import { UpdatePhoneNumberData } from '@src/components/sendMoneyFlow/components/phoneNumberUpdate';
import { addTelCode } from '@src/utils/getCountryTelCode';
import { SetAddressFormData } from '@src/components/sendMoneyFlow/types';
import { useGenerateOtp, useVerifyOtp } from '../queries/account';

type GenerateOtpPayloadI = Omit<ClientApi.GenerateOtpI, 'otp'>;
export type UpdateType = 'phoneUpdate' | 'setAddress';

interface UseAddressUpdateOptions {
  type: UpdateType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-unused-vars
  onSubmitSuccess?: (value: any) => void;
  hash?: string;
  email?: string;
}

export type StepsForPhoneUpdateType = 'initial' | 'generated' | 'verified';

export interface UseAddressUpdateResponse {
  phoneUpdateLoading: boolean;
  verifyStatus: boolean | null;
  isPhoneNumberModalShown: boolean;
  isPhoneNumberSuccessModalShown: boolean;
  handleAddressUpdate: () => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleUserDetailsUpdate: (values: any, helpers: any) => Promise<void>;
  formState: SetAddressFormData | undefined;
  handleChangePhoneNo: () => void;
  handlePhoneNumberSuccessClose: () => void;
  stepsForPhoneUpdate: StepsForPhoneUpdateType;
  setIsPhoneNumberModalShown: React.Dispatch<React.SetStateAction<boolean>>;
  handlePhoneNumberClose: () => void;
  addressResponse: ClientApi.UserAddress | undefined;
}

/**
 * custom hook to handle address and phone number update
 * it also handles the otp logic
 */
const useAddressUpdate = ({
  onSubmitSuccess,
  type,
  hash,
  email
}: UseAddressUpdateOptions): UseAddressUpdateResponse => {
  const [phoneUpdateLoading, setPhoneUpdateLoading] = useState(false);
  const [stepsForPhoneUpdate, setStepsForPhoneUpdate] =
    useState<StepsForPhoneUpdateType>('initial');
  const [verifyStatus, setVerifyStatus] = useState<boolean | null>(null);
  const [phoneNumberVal, setPhoneNumberVal] = useState('');
  const [isPhoneNumberModalShown, setIsPhoneNumberModalShown] = useState(false);
  const [isPhoneNumberSuccessModalShown, setIsPhoneNumberSuccessModalShown] = useState(false);
  const [formState, setFormState] = useState<SetAddressFormData>();
  const [addressResponse, setAddressResponse] = useState<ClientApi.UserAddress>();

  const { mixpanelInstance } = useMixpanel();
  const { userAddress, setAddress, user } = useAccount();
  const { mutateAsync: generateOtpMutateAsync } = useGenerateOtp();
  const { mutateAsync: verifyOtpMutateAsync } = useVerifyOtp();
  const state = getState();

  const handlePhoneNumberClose = () => {
    setIsPhoneNumberModalShown(false);
    setStepsForPhoneUpdate('initial');
  };

  const handlePhoneNumberSuccessClose = () => {
    setIsPhoneNumberSuccessModalShown(false);
  };

  const handleGenerateOtp = async (
    payload: GenerateOtpPayloadI,
    values: UpdatePhoneNumberData | SetAddressFormData
  ) => {
    setPhoneUpdateLoading(true);
    try {
      const res = await generateOtpMutateAsync({
        ...payload,
        hash: hash || payload.hash,
        email: email || payload.email
      });
      const addressValues = values as SetAddressFormData;

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.Successful) {
        const countryCode =
          type === 'phoneUpdate' ? state?.address?.country : addressValues.country;

        setIsPhoneNumberModalShown(true);
        setPhoneNumberVal(addTelCode(countryCode as Api.Model.CountryInitials, values.phone));
        setStepsForPhoneUpdate('generated');

        mixpanelInstance.track(mixpanelEvents.GenerateOTPSuccess);
      }

      if (type === 'phoneUpdate') {
        setFormState({
          city: userAddress?.city || '',
          country: userAddress?.country as Api.Model.CountryInitials,
          phone: addTelCode(state?.address?.country as Api.Model.CountryInitials, values.phone),
          postCode: userAddress?.postCode || '',
          streetAddress: userAddress?.street || '',
          dateOfBirth: userAddress?.dateOfBirth || '',
          state: userAddress?.state || '',
          ssn: userAddress?.ssn || ''
        });
      }

      if (type === 'setAddress') {
        setFormState({
          city: addressValues?.city || '',
          country: addressValues?.country as Api.Model.CountryInitials,
          phone: addTelCode(addressValues?.country as Api.Model.CountryInitials, values.phone),
          postCode: addressValues?.postCode || '',
          streetAddress: addressValues?.streetAddress || '',
          dateOfBirth: addressValues?.dateOfBirth || '',
          state: addressValues?.state || '',
          ssn: addressValues?.ssn || ''
        });
      }

      setPhoneUpdateLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setPhoneUpdateLoading(false);
      mixpanelInstance.track(mixpanelEvents.GenerateOTPFailed, {
        error: e?.ResponseMessage || 'Error occured'
      });
    }
  };

  const handleVerifyOtp = async (payload: ClientApi.GenerateOtpI) => {
    setPhoneUpdateLoading(true);
    try {
      const res = await verifyOtpMutateAsync({
        ...payload,
        hash: hash || payload.hash,
        email: email || payload.email
      });

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.Successful) {
        setStepsForPhoneUpdate('verified');
        setVerifyStatus(true);
        mixpanelInstance.track(mixpanelEvents.VerifyOTPSuccess);
      }

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.DataNotFound) {
        setVerifyStatus(false);
        mixpanelInstance.track(mixpanelEvents.VerifyOTPFailed);
      }

      setPhoneUpdateLoading(false);
    } catch (error) {
      setPhoneUpdateLoading(false);
      mixpanelInstance.track(mixpanelEvents.VerifyOTPFailed);
    }
  };

  /**
   * handles generating otp and verifying otp
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUserDetailsUpdate = async (values: any, helpers: any) => {
    const hashVal = (state && state.hash) || hash || '';

    setPhoneUpdateLoading(true);
    if (stepsForPhoneUpdate === 'initial') {
      const countryCode = type === 'phoneUpdate' ? state?.address?.country : values?.country;

      const payload: GenerateOtpPayloadI = {
        email: user?.email || '',
        hash: hashVal,
        phone: addTelCode(countryCode, values.phone),
        purpose: 1
      };

      helpers.setTouched({
        otp: false
      });

      handleGenerateOtp(payload, values);
    } else if (stepsForPhoneUpdate === 'generated') {
      const countryCode =
        type === 'phoneUpdate'
          ? state?.address?.country || Api.Model.CountryInitials.UnitedKingdom
          : formState?.country || Api.Model.CountryInitials.UnitedKingdom;

      const payload: ClientApi.GenerateOtpI = {
        email: user?.email || '',
        hash: hashVal,
        phone: addTelCode(countryCode, values.phone),
        otp: values.otp || '',
        purpose: 0
      };

      handleVerifyOtp(payload);
    }
  };

  /**
   * handles the final address update
   */
  const handleAddressUpdate = useCallback(async () => {
    setPhoneUpdateLoading(true);
    const payload: Omit<SetAddressFormData, 'twilioTerms'> = {
      city: formState?.city || '',
      country: formState?.country as Api.Model.CountryInitials,
      phone: formState?.phone || phoneNumberVal,
      postCode: formState?.postCode || '',
      streetAddress: formState?.streetAddress || '',
      dateOfBirth: formState?.dateOfBirth || '',
      state: formState?.state || '',
      ssn: formState?.ssn || ''
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await setAddress({ ...payload, hash, email } as any);

      if (res) {
        setAddressResponse(res);
      }

      handlePhoneNumberClose();
      setIsPhoneNumberSuccessModalShown(true);
      setPhoneUpdateLoading(false);
      if (onSubmitSuccess) {
        onSubmitSuccess(payload);
      }
    } catch (e) {
      setPhoneUpdateLoading(false);
      // helpers.setSubmitting(false);
    }
  }, [
    formState?.city,
    formState?.country,
    formState?.phone,
    formState?.postCode,
    formState?.streetAddress,
    phoneNumberVal,
    setAddress
  ]);

  /**
   * handles change phone number btn
   */
  const handleChangePhoneNo = () => {
    setStepsForPhoneUpdate('initial');
    setIsPhoneNumberModalShown(false);
    setVerifyStatus(null);
  };

  /**
   * effect that triggers the final address update
   */
  useEffect(() => {
    if (stepsForPhoneUpdate === 'verified') {
      handleAddressUpdate();
    }
  }, [handleAddressUpdate, stepsForPhoneUpdate]);

  return {
    phoneUpdateLoading,
    verifyStatus,
    isPhoneNumberModalShown,
    isPhoneNumberSuccessModalShown,
    handleAddressUpdate,
    handleUserDetailsUpdate,
    formState,
    handleChangePhoneNo,
    handlePhoneNumberSuccessClose,
    stepsForPhoneUpdate,
    setIsPhoneNumberModalShown,
    handlePhoneNumberClose,
    addressResponse
  };
};

export default useAddressUpdate;
